import { useEffect, useState } from "react";
import { api } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { Plus, Trash2, Save } from "lucide-react";

export default function RewardUnlocksManager() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    const { data } = await api.get("/admin/rewards/unlocks");
    setItems(data || []);
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const upd = (id, k, v) => setItems((p) => p.map((x) => x.id === id ? { ...x, [k]: v } : x));

  const save = async (r) => {
    try {
      await api.put(`/admin/rewards/unlocks/${r.id}`, {
        slug: r.slug, name: r.name, description: r.description || "",
        points_required: Number(r.points_required) || 0,
        promo_percent: Number(r.promo_percent) || 0,
        promo_amount_off: Number(r.promo_amount_off) || 0,
        notify_message: r.notify_message || "",
        email_subject: r.email_subject || "",
        email_body: r.email_body || "",
        content_ids: r.content_ids || [],
        is_active: r.is_active !== false,
      });
      toast.success(`${r.name} saved`);
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
  };

  const add = async () => {
    const slug = prompt("Reward slug (e.g., black-friday-vip):");
    if (!slug) return;
    try {
      const { data } = await api.post("/admin/rewards/unlocks", {
        slug, name: slug, description: "", points_required: 1000,
        promo_percent: 10, promo_amount_off: 0, notify_message: "",
        email_subject: "", email_body: "", content_ids: [], is_active: true,
      });
      setItems((p) => [...p, data]);
      toast.success("Created");
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const del = async (id, name) => {
    if (!window.confirm(`Delete reward "${name}"?`)) return;
    await api.delete(`/admin/rewards/unlocks/${id}`);
    setItems((p) => p.filter((x) => x.id !== id));
  };

  if (loading) return <div className="text-white/40">Loading…</div>;
  return (
    <div className="space-y-6" data-testid="rewards-unlocks-manager">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="font-heading text-2xl text-white tracking-wider">Reward Unlocks</h2>
          <p className="text-white/40 text-xs mt-1">Automatic Stripe promo codes + notifications when members hit point thresholds.</p>
        </div>
        <button onClick={add} data-testid="add-reward-btn" className="text-xs bg-[#D4AF37] text-black px-4 py-2 hover:bg-white uppercase tracking-widest flex items-center gap-2">
          <Plus className="w-3.5 h-3.5" /> Add Reward
        </button>
      </div>
      <div className="grid gap-4">
        {items.map((r) => (
          <div key={r.id} className="bg-[#0A0A0A] border border-white/5 rounded p-5" data-testid={`reward-row-${r.slug}`}>
            <div className="grid md:grid-cols-4 gap-3 mb-3">
              <input className="input-dark" value={r.name} placeholder="Name" onChange={(e) => upd(r.id, "name", e.target.value)} />
              <input className="input-dark" value={r.slug} placeholder="slug" onChange={(e) => upd(r.id, "slug", e.target.value)} />
              <input className="input-dark" type="number" value={r.points_required} placeholder="Points required" onChange={(e) => upd(r.id, "points_required", e.target.value)} data-testid={`reward-points-${r.slug}`} />
              <label className="flex items-center gap-2 text-white text-sm">
                <input type="checkbox" checked={r.is_active !== false} onChange={(e) => upd(r.id, "is_active", e.target.checked)} /> Active
              </label>
            </div>
            <textarea className="input-dark w-full mb-3" rows={2} placeholder="Description" value={r.description || ""} onChange={(e) => upd(r.id, "description", e.target.value)} />
            <div className="grid md:grid-cols-2 gap-3 mb-3">
              <div>
                <label className="text-[10px] uppercase tracking-widest text-white/40 mb-1 block">Stripe % off</label>
                <input className="input-dark w-full" type="number" value={r.promo_percent || 0} onChange={(e) => upd(r.id, "promo_percent", e.target.value)} />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-widest text-white/40 mb-1 block">or $ off</label>
                <input className="input-dark w-full" type="number" value={r.promo_amount_off || 0} onChange={(e) => upd(r.id, "promo_amount_off", e.target.value)} />
              </div>
            </div>
            <input className="input-dark w-full mb-3" placeholder="In-app notification message" value={r.notify_message || ""} onChange={(e) => upd(r.id, "notify_message", e.target.value)} />
            <input className="input-dark w-full mb-3" placeholder="Email subject (optional)" value={r.email_subject || ""} onChange={(e) => upd(r.id, "email_subject", e.target.value)} />
            <div className="flex justify-end gap-2">
              <button onClick={() => del(r.id, r.name)} className="text-xs text-red-400 hover:text-red-300 flex items-center gap-1 px-3 py-1.5"><Trash2 className="w-3.5 h-3.5" /> Delete</button>
              <button onClick={() => save(r)} data-testid={`reward-save-${r.slug}`} className="text-xs bg-[#D4AF37] text-black px-4 py-2 hover:bg-white uppercase tracking-widest flex items-center gap-2"><Save className="w-3.5 h-3.5" /> Save</button>
            </div>
          </div>
        ))}
      </div>
      <style>{`.input-dark { background:#000; border:1px solid rgba(255,255,255,0.1); color:#fff; padding:10px 12px; font-size:13px; border-radius:4px; outline:none; } .input-dark:focus { border-color:#D4AF37; }`}</style>
    </div>
  );
}
